/**
 * ScrollToTop
 *
 * Resets the document scroll position on client-side route changes.
 *
 * Business rule:
 * Catalog shops (e-cards, flowers, mailed cards) use window scrolling with
 * infinite scroll. Without an explicit reset, navigating via the header brand
 * link (or any other in-app link) keeps the previous page's scroll offset —
 * so the public landing can open already scrolled to the bottom.
 *
 * Hash-only navigations on the same path+search (e.g. `/` → `/#landing-explore`)
 * are left alone so section anchors still work. Path or search changes always
 * reset, even when a hash is present (e.g. shop → `/?tab=…#landing-explore`).
 * Mount once inside {@link BrowserRouter}.
 */

import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { scrollWindowToTop } from '../../utils/scrollWindowToTop';

/**
 * Scrolls the window to the top whenever the path or search string changes.
 */
export const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();
  const previousPathSearchRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const pathSearch = `${pathname}${search}`;
    const previous = previousPathSearchRef.current;
    previousPathSearchRef.current = pathSearch;

    // First paint on a fresh load — still reset so a restored session does not
    // leave the user mid-page when React hydrates over a tall previous view.
    if (previous === null) {
      if (!hash) {
        scrollWindowToTop('instant');
      }
      return;
    }

    // Same path+search, hash-only change — keep current offset for anchors.
    if (previous === pathSearch) {
      return;
    }

    scrollWindowToTop('instant');
  }, [pathname, search, hash]);

  return null;
};
